// src/pages/clientes/components/FilialFormDialog.tsx
import { useState, useEffect } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { Store, Save, Loader2, Info, Building2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/campos-formulario/novos/FormInput";
import { FormSelect } from "@/components/campos-formulario/novos/FormSelect";

import { ClienteFilialDTO } from "@/types/cliente-filial.type";
import { ClienteDTO } from "@/types/cliente.type";
import { useFilialForm } from "@/hooks/clientes/use-filial-form";
import { clienteService } from "@/services/api/cliente.service";

interface FilialFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filialToEdit?: ClienteFilialDTO | null;
}

export function FilialFormDialog(props: FilialFormDialogProps) {
  const { form, isSubmitting, onSubmit } = useFilialForm(props);
  const {
    control,
    formState: { errors, isValid },
  } = form;

  const [matrizes, setMatrizes] = useState<ClienteDTO[]>([]);
  const [isLoadingMatrizes, setIsLoadingMatrizes] = useState(false);

  // Carrega as matrizes ativas para o select de vínculo
  useEffect(() => {
    if (props.open) {
      const fetchMatrizes = async () => {
        setIsLoadingMatrizes(true);
        try {
          const res = await clienteService.listClientes({ limit: 500, ativo: true });
          setMatrizes(res.data);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoadingMatrizes(false);
        }
      };
      fetchMatrizes();
    }
  }, [props.open]);

  const selectOptions = matrizes.map((m) => ({
    label: m.nome,
    value: m.id.toString(),
  }));

  return (
    <Dialog open={props.open} onOpenChange={(val) => !isSubmitting && props.onOpenChange(val)}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden rounded-xl border-border bg-background p-0 shadow-2xl sm:max-w-[500px] sm:rounded-2xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Editar Filial</DialogTitle>
          <DialogDescription>Altere as informações ou o vínculo da filial.</DialogDescription>
        </DialogHeader>

        <div className="z-20 flex shrink-0 items-center justify-between border-b bg-card px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600/10">
              <Store className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="mb-1 text-base font-bold leading-none tracking-tight text-foreground sm:text-lg">
                Editar Filial
              </h2>
              <p className="text-[10px] text-muted-foreground sm:text-xs">
                Publi ID:{" "}
                <span className="font-medium text-foreground">{props.filialToEdit?.idPubli}</span>
              </p>
            </div>
          </div>
        </div>

        <FormProvider {...form}>
          <form
            onSubmit={onSubmit}
            className="flex flex-1 flex-col overflow-hidden"
            autoComplete="off"
            noValidate
          >
            <div className="custom-scrollbar space-y-6 overflow-y-auto p-5 sm:p-6">
              <section className="space-y-4">
                <div className="flex items-center gap-2 border-b border-border/50 pb-2">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Dados da Filial
                  </h3>
                </div>

                <div className="space-y-5">
                  <Controller
                    name="nome"
                    control={control}
                    render={({ field }) => (
                      <FormInput
                        id="nome"
                        label="Nome da Filial"
                        required
                        {...field}
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={errors.nome?.message}
                      />
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <Controller
                      name="cnpj"
                      control={control}
                      render={({ field }) => (
                        <FormInput
                          id="cnpj"
                          label="CNPJ"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="00.000.000/0001-00"
                          error={errors.cnpj?.message}
                        />
                      )}
                    />

                    <Controller
                      name="grupo"
                      control={control}
                      render={({ field }) => (
                        <FormInput
                          id="grupo"
                          label="Grupo"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value)}
                          error={errors.grupo?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-2 border-b border-border/50 pb-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Vínculo com Matriz
                  </h3>
                </div>

                <Controller
                  name="clienteId"
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      label="Cliente Matriz"
                      placeholder="Sem vínculo. Selecione para vincular..."
                      options={selectOptions}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isLoadingMatrizes}
                    />
                  )}
                />
              </section>
            </div>

            <div className="z-20 mt-auto flex flex-col-reverse justify-end gap-3 border-t bg-muted/10 p-5 sm:p-6 md:flex-row">
              <Button
                type="button"
                variant="outline"
                className="w-full bg-background shadow-sm hover:bg-muted md:w-32"
                onClick={() => props.onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                className="group w-full gap-2 bg-blue-600 font-bold text-white shadow-sm hover:bg-blue-700 md:w-40"
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Salvando...
                  </>
                ) : (
                  <>
                    <span>Salvar Filial</span>
                    <Save className="ml-1 h-4 w-4 opacity-70 group-hover:opacity-100" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
